import React from "react";
import PropTypes from "prop-types";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReload = () => {
    window.location?.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const { fallback } = this.props;
      if (fallback) {
        return typeof fallback === "function"
          ? fallback(this.state.error, this.handleReset)
          : fallback;
      }

      const isDev = process.env.NODE_ENV === "development";

      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            textAlign: "center",
            backgroundColor: "#f8f9fa",
            fontFamily:
              "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          }}
        >
          <div
            style={{
              maxWidth: "480px",
              padding: "2rem",
              backgroundColor: "#fff",
              borderRadius: "8px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            }}
          >
            <div
              style={{
                fontSize: "3rem",
                marginBottom: "1rem",
                lineHeight: 1,
              }}
              aria-hidden
            >
              ⚠️
            </div>
            <h1
              style={{
                fontSize: "1.5rem",
                marginBottom: "0.5rem",
                color: "#212529",
              }}
            >
              Something went wrong
            </h1>
            <p
              style={{
                color: "#6c757d",
                marginBottom: "1.5rem",
                lineHeight: 1.5,
              }}
            >
              An unexpected error occurred. Please try reloading the page.
            </p>
            {isDev && this.state.error && (
              <details
                style={{
                  textAlign: "left",
                  marginBottom: "1.5rem",
                  padding: "1rem",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "4px",
                  fontSize: "0.875rem",
                }}
              >
                <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                  Error details
                </summary>
                <pre
                  style={{
                    marginTop: "0.5rem",
                    overflow: "auto",
                    color: "#dc3545",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {this.state.error.toString()}
                </pre>
                {this.state.errorInfo?.componentStack && (
                  <pre
                    style={{
                      marginTop: "0.5rem",
                      overflow: "auto",
                      fontSize: "0.75rem",
                      color: "#6c757d",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </details>
            )}
            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={this.handleReload}
                style={{
                  padding: "0.5rem 1.25rem",
                  fontSize: "1rem",
                  fontWeight: 500,
                  color: "#fff",
                  backgroundColor: "#0d6efd",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Reload page
              </button>
              {isDev && (
                <button
                  type="button"
                  onClick={this.handleReset}
                  style={{
                    padding: "0.5rem 1.25rem",
                    fontSize: "1rem",
                    fontWeight: 500,
                    color: "#0d6efd",
                    backgroundColor: "transparent",
                    border: "1px solid #0d6efd",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  Try again
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};

export default ErrorBoundary;
